// How the Tasks page lists unfinished tasks in each view. Pure; tested beside this file.
import { byDeadline } from "./goalOrder";
import { inCategory, NO_CATEGORY, NO_CATEGORY_LABEL } from "../../shared/categories";

export const OLDEST_AFTER_DAYS = 7; // "a week or more" on the Tasks page
export const OLDEST_COUNT = 10;
const DAY_MS = 24 * 60 * 60 * 1000;

const open = (tasks) => (tasks || []).filter(t => !t.done);
const createdMs = (t) => (t.createdAt?.seconds ?? 0) * 1000;

// Whole days since a task was added.
export const daysOnList = (task, now = Date.now()) => Math.floor((now - createdMs(task)) / DAY_MS);

// A to Z.
export const allTasks = (tasks) => open(tasks).sort((a, b) => (a.text || "").localeCompare(b.text || ""));

// Soonest deadline first, undated last.
export const deadlineTasks = (tasks, goals) => open(tasks).sort(byDeadline(goals));

// The tasks that have waited longest, once they've been on the list for a week or more.
export const oldestTasks = (tasks, now = Date.now()) =>
  open(tasks).filter(t => t.createdAt && daysOnList(t, now) >= OLDEST_AFTER_DAYS)
    .sort((a, b) => createdMs(a) - createdMs(b)).slice(0, OLDEST_COUNT);

// Categories in the user's order, each with its tasks by deadline; categories with none are left out. Tasks
// with no category, or one that no longer exists, come last under `id: null`.
export const tasksByCategory = (tasks, goals, categories) => {
  const sort = byDeadline(goals);
  const groups = (categories || []).map(cat => ({ category: cat, tasks: open(tasks).filter(t => t.categoryId === cat.id).sort(sort) }));
  const other = open(tasks).filter(t => inCategory(t, NO_CATEGORY, categories)).sort(sort);
  return [...groups, ...(other.length ? [{ category: { id: null, label: NO_CATEGORY_LABEL }, tasks: other }] : [])].filter(g => g.tasks.length > 0);
};

// The whole category list after dragging one shown category onto another: categories with no tasks (not shown)
// keep their place after the shown ones.
export const moveCategory = (categories, shownIds, fromId, toId) => {
  const moved = [...shownIds];
  const to = shownIds.indexOf(toId);
  moved.splice(to, 0, moved.splice(shownIds.indexOf(fromId), 1)[0]);
  const byId = new Map(categories.map(c => [c.id, c]));
  return [...moved.map(id => byId.get(id)), ...categories.filter(c => !shownIds.includes(c.id))];
};

// Finished tasks, most recently changed first.
export const doneTasks = (tasks) => (tasks || []).filter(t => t.done)
  .sort((a, b) => (b.updatedAt?.seconds ?? 0) - (a.updatedAt?.seconds ?? 0));
